import React, { useCallback, useEffect, useRef, useState, forwardRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import throttle from "lodash.throttle";
import ScrollLink from "../components/scroll-link";

const ScrollTabs = forwardRef(
  ({ tabs, activeTab, clickTab, sticky, className, style, isProduct, noHeroTop }, ref) => {
    const scrollableRef = useRef();

    const slidePrev = useCallback(() => {
      const target = [...scrollableRef.current.children]
        .reverse()
        .find((child) => child.getBoundingClientRect().left < 0);
      if (target) {
        scrollableRef.current.scrollTo({
          left: scrollableRef.current.scrollLeft + target.getBoundingClientRect().left - 35,
          behavior: "smooth",
        });
      }
    }, []);
    const slideNext = useCallback(() => {
      const width = scrollableRef.current.getBoundingClientRect().width;
      const target = [...scrollableRef.current.children].find(
        (child) => child.getBoundingClientRect().right > width
      );
      if (target) {
        scrollableRef.current.scrollTo({
          left:
            scrollableRef.current.scrollLeft + target.getBoundingClientRect().right - width + 35,
          behavior: "smooth",
        });
      }
    }, []);

    const [showPrev, setShowPrev] = useState(false);
    const [showNext, setShowNext] = useState(false);

    useEffect(() => {
      const scrollableElem = scrollableRef.current;
      if (scrollableElem) {
        const listener = throttle(() => {
          setShowPrev(scrollableElem.scrollLeft > 0);
          setShowNext(
            scrollableElem.children[scrollableElem.children.length - 1].getBoundingClientRect()
              .right >
              scrollableElem.getBoundingClientRect().right + 10
          );
        }, 100);
        scrollableElem.addEventListener("scroll", listener);
        window.addEventListener("resize", listener);
        listener();
        return () => {
          scrollableElem.removeEventListener("scroll", listener);
          window.removeEventListener("resize", listener);
        };
      }
    }, []);

    const [openMobile, setOpenMobile] = useState(false);
    const toggleOpenMobile = useCallback(() => setOpenMobile((value) => !value), []);
    const currentTab = tabs.find((tab) => tab.id === activeTab);

    return (
      <div
        ref={ref}
        className={classNames(
          `tabs`,
          className,
          sticky && "tabs--sticky",
          noHeroTop && "tabs--sticky-no-hero-top",
          isProduct && "tabs--pdp"
        )}
        style={style}
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 px-0">
              <div className="tabs__slider d-none d-md-block">
                <button
                  className="tabs__arrow tabs__arrow--prev"
                  aria-label="prev"
                  disabled={!showPrev}
                  onClick={slidePrev}
                />
                <div ref={scrollableRef} className="tabs__scrollable">
                  {tabs.map((tab) => (
                    <div
                      className={classNames("tabs__tab", activeTab === tab.id && "active")}
                      key={tab.id}
                    >
                      {clickTab ? (
                        <button
                          name={tab.id}
                          className="tabs__inner tabs__inner--pdp"
                          onClick={clickTab}
                        >
                          <span className={tab.labelMobile ? "d-none d-lg-inline" : ""}>
                            {tab.label}
                          </span>
                          {tab.labelMobile && <span className="d-lg-none">{tab.labelMobile}</span>}
                        </button>
                      ) : (
                        <ScrollLink href={`#${tab.id}`} className="tabs__inner tabs__inner--pdp">
                          <span className={tab.labelMobile ? "d-none d-lg-inline" : ""}>
                            {tab.label}
                          </span>
                          {tab.labelMobile && <span className="d-lg-none">{tab.labelMobile}</span>}
                        </ScrollLink>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  className="tabs__arrow tabs__arrow--next"
                  aria-label="next"
                  disabled={!showNext}
                  onClick={slideNext}
                />
              </div>
              <div
                className={classNames("tabs__mobile d-md-none", openMobile && "tabs__mobile--open")}
              >
                <button
                  onClick={toggleOpenMobile}
                  className="tabs__mobile-toggle d-md-none tabs__mobile-toggle--pdp"
                >
                  {currentTab ? currentTab.labelMobile || currentTab.label : ""}
                </button>
                <ul
                  className={classNames(
                    "tabs__mobile-list",
                    !openMobile && "d-md-flex justify-content-center"
                  )}
                >
                  {tabs.map((tab) => (
                    <li
                      className={classNames(
                        "tabs__tab tabs__tab--mobile",
                        activeTab === tab.id && "active"
                      )}
                      key={tab.id}
                    >
                      {clickTab ? (
                        <button
                          name={tab.id}
                          className="tabs__inner tabs__inner--pdp"
                          onClick={(e) => {
                            clickTab(e);
                            setOpenMobile(false);
                          }}
                        >
                          <span className={tab.labelMobile ? "d-none d-lg-inline" : ""}>
                            {tab.label}
                          </span>
                          {tab.labelMobile && <span className="d-lg-none">{tab.labelMobile}</span>}
                        </button>
                      ) : (
                        <ScrollLink
                          href={`#${tab.id}`}
                          className="tabs__inner tabs__inner--pdp"
                          onClick={() => setOpenMobile(false)}
                        >
                          <span className={tab.labelMobile ? "d-none d-lg-inline" : ""}>
                            {tab.label}
                          </span>
                          {tab.labelMobile && <span className="d-lg-none">{tab.labelMobile}</span>}
                        </ScrollLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default ScrollTabs;

ScrollTabs.propTypes = {
  className: PropTypes.string,
  sticky: PropTypes.bool,
  activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  clickTab: PropTypes.func,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      labelMobile: PropTypes.string,
    })
  ),
};
